/**
 * Centralized chart color palette.
 *
 * This is the SINGLE SOURCE OF TRUTH for all data visualization colors.
 * Every chart component must import colors from here — never hardcode hex values.
 *
 * Color choices follow the reference design's visual language:
 * - Primary data: teal/blue tones
 * - Secondary data: orange/amber tones
 * - Tertiary data: purple/violet tones
 * - Warnings/alerts: red/coral tones
 * - Manual/user data: green tones
 */

// ── Device / Data Source Colors ──
// Used in dashboard charts (pH, Alkalinity, Salinity, Temperature)
// to distinguish data from different monitoring devices.

export const DEVICE_COLORS = {
  apex:          'hsl(30, 95%, 55%)',    // Orange — Apex Fusion probes
  apex2:         'hsl(0, 75%, 50%)',     // Red — Apex secondary probe
  alkatronic:    'hsl(185, 75%, 42%)',   // Teal — Focustronic Alkatronic
  mastertronic:  'hsl(0, 75%, 50%)',     // Red — Focustronic Mastertronic
  aquawiz:       'hsl(145, 65%, 40%)',   // Green — AquaWiz KH1
  aquarimate:    'hsl(270, 60%, 55%)',   // Purple — Aquarimate
  manual:        'hsl(210, 80%, 50%)',   // Blue — Manual measurements
  kamoer_kh:     'hsl(340, 65%, 50%)',   // Rose — Kamoer KH Carer
  kamoer_stp:    'hsl(198, 76%, 42%)',   // Steel blue — Kamoer STP dosing
  kamoer_p4:     'hsl(198, 76%, 42%)',   // Steel blue — Kamoer P4 dosing
  sensor:        'hsl(145, 65%, 40%)',   // Green — Generic sensor data
} as const;

// ── Dosing Chemical Colors ──
// Used in dosing charts and dosing event tables.

export const CHEMICAL_COLORS: Record<string, string> = {
  'Alkalinity':   'hsl(210, 70%, 55%)',  // Blue
  'Calcium-Mg':   'hsl(145, 60%, 45%)',  // Green
  'NoPox':        'hsl(25, 95%, 55%)',   // Orange
  'Phosphate':    'hsl(0, 70%, 50%)',    // Red
  'Unused':       'hsl(210, 10%, 65%)',  // Gray
} as const;

// ── Analysis Series Colors ──
// Rotating palette for analysis page charts when multiple series are displayed.
// Order matters: first series gets first color, etc.

export const ANALYSIS_SERIES_COLORS = [
  'hsl(200, 85%, 50%)',  // Sky blue
  'hsl(145, 60%, 45%)',  // Green
  'hsl(35, 95%, 55%)',   // Amber
  'hsl(270, 55%, 55%)',  // Violet
  'hsl(0, 70%, 55%)',    // Red
  'hsl(185, 85%, 42%)',  // Cyan
  'hsl(25, 95%, 55%)',   // Orange
  'hsl(280, 60%, 60%)',  // Purple
  'hsl(330, 75%, 55%)',  // Pink
  'hsl(170, 65%, 45%)',  // Teal
] as const;

// ── Analysis Dosing Chart Colors ──
// Same palette as ANALYSIS_SERIES_COLORS for dosing overlay charts.

export const ANALYSIS_DOSING_COLORS = ANALYSIS_SERIES_COLORS;

/**
 * Resolve a distinct color for a dosing chemical.
 *
 * Known chemicals (see CHEMICAL_COLORS) keep their curated color. Any other
 * chemical name — there are far more in practice than the curated map covers —
 * is assigned a distinct color from the rotating ANALYSIS_SERIES_COLORS palette
 * based on its index, so every series on a chart gets its own color instead of
 * collapsing onto a single fallback gray.
 *
 * @param chemical Server-resolved chemical name.
 * @param index    Stable position of the chemical within the chart's series
 *                 (e.g. its index in the sorted list of chemicals).
 */
export function getChemicalColor(chemical: string, index: number): string {
  return (
    CHEMICAL_COLORS[chemical] ??
    ANALYSIS_SERIES_COLORS[index % ANALYSIS_SERIES_COLORS.length]!
  );
}

// ── Maintenance Category Colors ──
// Used in maintenance charts, task event markers, and category badges.

export const MAINTENANCE_CATEGORY_COLORS: Record<string, string> = {
  cleaning:      'hsl(210, 70%, 55%)',  // Blue
  calibration:   'hsl(280, 55%, 45%)',  // Purple
  replacement:   'hsl(25, 95%, 55%)',   // Orange
  media_change:  'hsl(145, 60%, 45%)',  // Green
  water_change:  'hsl(185, 85%, 42%)',  // Cyan
  inspection:    'hsl(45, 95%, 50%)',   // Yellow
  dosing:        'hsl(330, 75%, 55%)',  // Pink
  testing:       'hsl(170, 65%, 45%)',  // Teal
  custom:        'hsl(210, 10%, 55%)',  // Gray
  feeding:       'hsl(270, 60%, 55%)',  // Purple
  miscellaneous: 'hsl(210, 10%, 50%)', // Gray
} as const;

// ── Maintenance Summary Colors ──
// Used in MaintenanceSummary.tsx metric cards.

export const MAINTENANCE_SUMMARY_COLORS = {
  total:      'hsl(210, 70%, 55%)',
  completed:  'hsl(145, 60%, 45%)',
  overdue:    'hsl(25, 95%, 55%)',
  compliance: 'hsl(270, 60%, 55%)',
} as const;

// ── Chart Structure Colors ──
// Crosshair, reference lines, fallback colors.

export const CHART_CROSSHAIR_COLOR = 'hsl(215, 15%, 60%)';
export const CHART_REFERENCE_LINE_COLOR = 'hsl(210, 10%, 80%)';
export const CHART_FALLBACK_COLOR = 'hsl(210, 10%, 50%)';
/** Green band marking a parameter's configured target range (matches ParameterDetailPage). */
export const TARGET_RANGE_BAND_COLOR = 'hsl(162, 73%, 46%)';

// ── ChartsPage Legend Colors ──
// Device/source legend colors for the main ChartsPage.

export const CHARTS_PAGE_DEVICE_COLORS: Record<string, { color: string; lightColor: string }> = {
  aquarimate:   { color: 'hsl(270, 55%, 55%)', lightColor: 'hsl(270, 55%, 75%)' },
  mastertronic: { color: 'hsl(0, 70%, 50%)',   lightColor: 'hsl(0, 70%, 75%)' },
  manual:       { color: 'hsl(210, 70%, 50%)', lightColor: 'hsl(210, 70%, 75%)' },
  aquawiz:      { color: 'hsl(145, 65%, 40%)', lightColor: 'hsl(145, 65%, 70%)' },
  kamoer_kh:    { color: 'hsl(340, 65%, 50%)', lightColor: 'hsl(340, 65%, 75%)' },
  kamoer_stp:   { color: 'hsl(198, 76%, 42%)', lightColor: 'hsl(198, 76%, 72%)' },
  kamoer_p4:    { color: 'hsl(198, 76%, 42%)', lightColor: 'hsl(198, 76%, 72%)' },
} as const;

// ── Event Line Color ──
// Used in analysis event vertical markers.

export const EVENT_LINE_COLOR = 'hsl(210, 10%, 60%)';
